import React from 'react';
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { ChartData, TechnicalIndicators, TimeRange } from '../types';

interface StockChartProps {
  data: ChartData[];
  indicators: TechnicalIndicators;
  timeRange: TimeRange;
  onTimeRangeChange: (range: TimeRange) => void;
}

const StockChart: React.FC<StockChartProps> = ({
  data,
  indicators,
  timeRange,
  onTimeRangeChange
}) => {
  const timeRanges: TimeRange[] = ['1D', '1W', '1M', '3M', '1Y', '5Y'];

  // Format data for chart
  const chartData = data.map(item => ({
    ...item,
    date: new Date(item.timestamp).toLocaleDateString(),
    time: new Date(item.timestamp).toLocaleTimeString(),
    rsi: indicators.rsi + (Math.random() - 0.5) * 10, // Add some variation
    macd: indicators.macd.macd + (Math.random() - 0.5) * 2,
    signal: indicators.macd.signal + (Math.random() - 0.5) * 2,
    histogram: indicators.macd.histogram + (Math.random() - 0.5) * 1,
    sma20: indicators.movingAverages.sma20 + (Math.random() - 0.5) * 10,
    sma50: indicators.movingAverages.sma50 + (Math.random() - 0.5) * 10
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="terminal-card p-3 text-sm">
          <p className="text-terminal-text font-semibold mb-2">{label}</p>
          <div className="space-y-1">
            <p className="text-terminal-muted">
              Open: <span className="text-terminal-text">${data.open?.toFixed(2)}</span>
            </p>
            <p className="text-terminal-muted">
              High: <span className="text-neon-green">${data.high?.toFixed(2)}</span>
            </p>
            <p className="text-terminal-muted">
              Low: <span className="text-neon-red">${data.low?.toFixed(2)}</span>
            </p>
            <p className="text-terminal-muted">
              Close: <span className="text-terminal-text font-semibold">${data.close?.toFixed(2)}</span>
            </p>
            <p className="text-terminal-muted">
              Volume: <span className="text-terminal-text">{(data.volume / 1000000).toFixed(1)}M</span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const RSITooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const rsi = payload[0].value;
      return (
        <div className="terminal-card p-2 text-sm">
          <p className="text-terminal-text">RSI: {rsi?.toFixed(2)}</p>
          <p className={`text-xs ${rsi > 70 ? 'text-neon-red' : rsi < 30 ? 'text-neon-green' : 'text-terminal-muted'}`}>
            {rsi > 70 ? 'Overbought' : rsi < 30 ? 'Oversold' : 'Neutral'}
          </p>
        </div>
      );
    }
    return null;
  };

  const MACDTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="terminal-card p-2 text-sm">
          <p className="text-terminal-text">MACD</p>
          <p className="text-neon-blue">Line: {payload[0]?.value?.toFixed(3)}</p>
          <p className="text-neon-yellow">Signal: {payload[1]?.value?.toFixed(3)}</p>
          <p className="text-terminal-muted">Histogram: {payload[2]?.value?.toFixed(3)}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-terminal-text">Price Chart</h3>
        <div className="flex gap-2">
          {timeRanges.map((range) => (
            <button
              key={range}
              onClick={() => onTimeRangeChange(range)}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                timeRange === range
                  ? 'bg-neon-blue text-black'
                  : 'bg-terminal-border text-terminal-muted hover:bg-terminal-muted/20 hover:text-terminal-text'
              }`}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      {/* Main Price Chart */}
      <div className="terminal-card p-4">
        <div className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
              <XAxis 
                dataKey="date" 
                stroke="#a3a3a3" 
                fontSize={12}
                tick={{ fill: '#a3a3a3' }}
              />
              <YAxis 
                stroke="#a3a3a3" 
                fontSize={12}
                tick={{ fill: '#a3a3a3' }}
                domain={['dataMin - 5', 'dataMax + 5']}
              />
              <Tooltip content={<CustomTooltip />} />
              
              {/* Candlestick representation using bars */}
              <Bar
                dataKey="close"
                fill="#00ff88"
                stroke="#00ff88"
                strokeWidth={1}
                opacity={0.8}
              />
              
              {/* Moving Averages */}
              <Line
                type="monotone"
                dataKey="sma20"
                stroke="#3742fa"
                strokeWidth={2}
                dot={false}
                strokeDasharray="5 5"
              />
              <Line
                type="monotone"
                dataKey="sma50"
                stroke="#ffa502"
                strokeWidth={2}
                dot={false}
                strokeDasharray="10 5"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        
        <div className="flex justify-center gap-6 mt-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-0.5 bg-neon-blue"></div>
            <span className="text-terminal-muted">SMA 20</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-0.5 bg-neon-yellow"></div>
            <span className="text-terminal-muted">SMA 50</span>
          </div>
        </div>
      </div>

      {/* Technical Indicators */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* RSI Chart */}
        <div className="terminal-card p-4">
          <h4 className="text-md font-semibold text-terminal-text mb-4">RSI (14)</h4>
          <div className="h-32 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                <XAxis 
                  dataKey="date" 
                  stroke="#a3a3a3" 
                  fontSize={10}
                  tick={{ fill: '#a3a3a3' }}
                />
                <YAxis 
                  stroke="#a3a3a3" 
                  fontSize={10}
                  tick={{ fill: '#a3a3a3' }}
                  domain={[0, 100]}
                />
                <Tooltip content={<RSITooltip />} />
                
                {/* RSI Levels */}
                <ReferenceLine y={70} stroke="#ff4757" strokeDasharray="2 2" />
                <ReferenceLine y={30} stroke="#00ff88" strokeDasharray="2 2" />
                <ReferenceLine y={50} stroke="#a3a3a3" strokeDasharray="1 1" />
                
                <Line
                  type="monotone"
                  dataKey="rsi"
                  stroke="#3742fa"
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-between text-xs text-terminal-muted mt-2">
            <span className="text-neon-red">Overbought (70)</span>
            <span className="text-neon-green">Oversold (30)</span>
          </div>
        </div>

        {/* MACD Chart */}
        <div className="terminal-card p-4">
          <h4 className="text-md font-semibold text-terminal-text mb-4">MACD</h4>
          <div className="h-32 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                <XAxis 
                  dataKey="date" 
                  stroke="#a3a3a3" 
                  fontSize={10}
                  tick={{ fill: '#a3a3a3' }}
                />
                <YAxis 
                  stroke="#a3a3a3" 
                  fontSize={10}
                  tick={{ fill: '#a3a3a3' }}
                />
                <Tooltip content={<MACDTooltip />} />
                
                <ReferenceLine y={0} stroke="#a3a3a3" strokeDasharray="1 1" />
                
                <Bar
                  dataKey="histogram"
                  fill="#a3a3a3"
                  opacity={0.6}
                />
                <Line
                  type="monotone"
                  dataKey="macd"
                  stroke="#3742fa"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="signal"
                  stroke="#ffa502"
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-4 text-xs text-terminal-muted mt-2">
            <div className="flex items-center gap-1">
              <div className="w-2 h-0.5 bg-neon-blue"></div>
              <span>MACD</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-0.5 bg-neon-yellow"></div>
              <span>Signal</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockChart;